import { useState, useEffect } from "react";
import "./Modal.css";

// Global handler for nested modals
class ModalManager {
	constructor() {
		this.openModals = new Map(); // modalId -> modalType
		this.modalCounts = {
			delete: 0,
			edit: 0,
			general: 0,
		};
	}

	addModal(modalId, modalType = "general") {
		this.openModals.set(modalId, modalType);
		this.modalCounts[modalType]++;

		// Add the appropriate CSS classes
		if (modalType === "delete" && this.modalCounts.delete === 1) {
			document.body.classList.add("delete-modal-open");
		} else if (modalType !== "delete" && this.getTotalNonDeleteModals() === 1) {
			document.body.classList.add("modal-open");
		}
	}

	removeModal(modalId) {
		const modalType = this.openModals.get(modalId);
		if (!modalType) return;

		this.openModals.delete(modalId);
		this.modalCounts[modalType]--;

		// Remove the appropriate CSS classes
		if (modalType === "delete" && this.modalCounts.delete === 0) {
			document.body.classList.remove("delete-modal-open");
		} else if (modalType !== "delete" && this.getTotalNonDeleteModals() === 0) {
			document.body.classList.remove("modal-open");
		}
	}

	getTotalNonDeleteModals() {
		return this.modalCounts.edit + this.modalCounts.general;
	}

	hasOpenModals() {
		return this.openModals.size > 0;
	}

	hasDeleteModalOpen() {
		return this.modalCounts.delete > 0;
	}
}

// Global instance of the manager
const modalManager = new ModalManager();

const Modal = ({
	isOpen,
	onClose,
	children,
	animated = true,
	modalType = "general",
}) => {
	const [shouldRender, setShouldRender] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);
	const [modalId] = useState(() => Math.random().toString(36).substr(2, 9));

	useEffect(() => {
		if (isOpen) {
			setShouldRender(true);
			modalManager.addModal(modalId, modalType);
			// Double requestAnimationFrame to ensure the DOM is fully rendered before starting animation
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setIsAnimating(true);
				});
			});
		} else if (animated) {
			setIsAnimating(false);
			modalManager.removeModal(modalId);
			// Exact duration of the CSS animation
			const timeout = setTimeout(() => setShouldRender(false), 300);
			return () => clearTimeout(timeout);
		} else {
			setShouldRender(false);
			modalManager.removeModal(modalId);
		}
	}, [isOpen, animated, modalId, modalType]);

	useEffect(() => {
		const handleEscape = e => {
			if (e.key === "Escape" && isOpen) {
				onClose();
			}
		};

		// Handle viewport height and scrolling for mobile browsers
		const handleResize = () => {
			if (isOpen) {
				// Update the custom CSS variable for viewport height
				const vh = window.innerHeight * 0.01;
				document.documentElement.style.setProperty("--vh", `${vh}px`);
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscape);

			// Mobile viewport management
			handleResize();
			window.addEventListener("resize", handleResize);
			window.addEventListener("orientationchange", handleResize);

			// Prevent scrolling on iOS Safari outside the modal
			const preventScroll = e => {
				if (e.target.closest(".modal")) return;
				e.preventDefault();
			};
			document.addEventListener("touchmove", preventScroll, { passive: false });

			return () => {
				document.removeEventListener("keydown", handleEscape);
				window.removeEventListener("resize", handleResize);
				window.removeEventListener("orientationchange", handleResize);
				document.removeEventListener("touchmove", preventScroll);
				if (!modalManager.hasOpenModals()) {
					document.documentElement.style.removeProperty("--vh");
				}
			};
		}

		return () => {
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen, onClose]);

	// Cleanup when disassembling the component
	useEffect(() => {
		return () => {
			if (isOpen) {
				modalManager.removeModal(modalId);
			}
		};
	}, [modalId, isOpen]);

	const handleOverlayClick = e => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	if (!shouldRender) return null;

	const renderChildren = () => {
		return children;
	};

	return (
		<div
			className={`modal-overlay ${isAnimating ? "visible" : "hidden"}`}
			data-modal-type={modalType}
			onClick={handleOverlayClick}
		>
			<div
				className={`modal ${isAnimating ? "visible" : "hidden"}`}
				onClick={e => e.stopPropagation()}
			>
				{renderChildren()}
			</div>
		</div>
	);
};

export default Modal;
